import { BlockedUsersProvider } from '@contexts/useBlockedUsers';
import { CurrentEventInfoProvider } from '@contexts/useCurrentEventInfo';
import { NotificationServiceProvider } from '@contexts/useNotificationService';
import { useCustomerIO } from '@hooks/useCustomerIO';
import { useHeapInitialization } from '@hooks/useHeapInitialization';
import useSetupAudio from '@hooks/useSetupAudio';
import { CommunityAuthController } from '@organisms/CommunityAuthController';
import { CommunityAuthFlowModal } from '@organisms/CommunityAuthFlowModal';
import { EventCheckInModalTrigger } from '@organisms/EventCheckInModalTrigger';
import * as Sentry from '@sentry/react-native';
import { ApiError, registerQueryClient } from '@sunrise-ui/api-client';
import {
  CheckoutModal,
  StripeProvider,
  useCheckoutRedirect,
  usePaymentSheetCleanup,
} from '@sunrise-ui/checkout';
import {
  AppDownloadBanner,
  SessionModalProvider,
  UpdateCheckGuard,
  VersionCheckGuard,
} from '@sunrise-ui/composites';
import {
  AppStateProvider,
  BannerProvider,
  EnableNotificationsModalController,
  ErrorBoundary,
  MediaDeviceProvider,
  MediaDeviceType,
  PushNotificationsProvider,
  ScrollPositionProvider,
  SnackbarProvider,
  ThemeAndPaperProvider,
  injectGlobalScrollbarStyles,
  useAuthStore,
  useSunriseFonts,
} from '@sunrise-ui/primitives';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Application from 'expo-application';
import { Slot } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import React, { useEffect } from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Portal } from 'react-native-paper';
import { en, registerTranslation } from 'react-native-paper-dates';

registerTranslation('en', en);

// Inject global scrollbar styles for web
injectGlobalScrollbarStyles();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        if (error instanceof ApiError) {
          return failureCount < 3 && error.retryable;
        }

        return failureCount < 3;
      },
      gcTime: 20 * 60 * 1000, // 20 minutes
      staleTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

registerQueryClient(queryClient);

export {
  // Catch any errors thrown by the Layout component.
  ErrorBoundary,
} from 'expo-router';

SplashScreen.setOptions({
  duration: 750,
  fade: true,
});

function RootLayout() {
  const [loaded, error] = useSunriseFonts();

  useSetupAudio();
  useHeapInitialization();
  useCustomerIO();

  // Expo Router uses Error Boundaries to catch errors in the navigation tree.
  useEffect(() => {
    if (error) throw error;
  }, [error]);

  if (!loaded) {
    return null;
  }

  return <RootLayoutNav />;
}

function RootLayoutNav() {
  return (
    <GestureHandlerRootView>
      <QueryClientProvider client={queryClient}>
        <ScrollPositionProvider>
          <AppStateProvider>
            <MediaDeviceProvider mediaDeviceType={MediaDeviceType.CAMERA}>
              <MediaDeviceProvider mediaDeviceType={MediaDeviceType.MICROPHONE}>
                <PushNotificationsProvider>
                  <CurrentEventInfoProvider>
                    <ErrorBoundaryStack />
                  </CurrentEventInfoProvider>
                </PushNotificationsProvider>
              </MediaDeviceProvider>
            </MediaDeviceProvider>
          </AppStateProvider>
        </ScrollPositionProvider>
      </QueryClientProvider>
    </GestureHandlerRootView>
  );
}

function ErrorBoundaryStack() {
  const { isAuthenticated, currentUserId, currentUserRole } = useAuthStore();

  useCheckoutRedirect();
  usePaymentSheetCleanup(isAuthenticated);

  return (
    <ErrorBoundary
      context={{
        appVersion: Application.nativeApplicationVersion || 'unknown',
        platform: process.env.EXPO_OS || 'unknown',
        timestamp: new Date().toISOString(),
        user: {
          isAuthenticated,
          userId: currentUserId || 'anonymous',
          userRole: currentUserRole,
        },
      }}
      onError={(error, errorInfo) => {
        console.error('App-wide error boundary caught error:', {
          error: error.message,
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          user: {
            isAuthenticated,
            userId: currentUserId || 'anonymous',
            userRole: currentUserRole,
          },
        });
      }}
    >
      <StripeProvider merchantIdentifier="merchant.sunrise.rsvp">
        <ThemeAndPaperProvider>
          <SnackbarProvider>
            <NotificationServiceProvider>
              <BannerProvider>
                <Portal.Host>
                  <CommunityAuthController />
                  <CommunityAuthFlowModal />
                  <UpdateCheckGuard />
                  <VersionCheckGuard />
                  <SessionModalProvider>
                    <BlockedUsersProvider>
                      <AppDownloadBanner />
                      <Portal.Host>
                        <EventCheckInModalTrigger />
                        <CheckoutModal />
                        <EnableNotificationsModalController />
                        <Slot />
                        {/* @ts-expect-error -- style prop incorrectly errors */}
                        <StatusBar style="light" />
                      </Portal.Host>
                    </BlockedUsersProvider>
                  </SessionModalProvider>
                </Portal.Host>
              </BannerProvider>
            </NotificationServiceProvider>
          </SnackbarProvider>
        </ThemeAndPaperProvider>
      </StripeProvider>
    </ErrorBoundary>
  );
}

export default Sentry.wrap(RootLayout);
